'use client'

import { useState, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import { X, Upload, CloudUpload, Library, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { pushModal } from '@/components/modals'
import { isHttps } from '@/lib/editor/utils'
import { UploadAssets } from '@/components/upload-assets'
import { ElementLibrary } from '@/components/element-library'
import { CreationLibrary } from '@/components/element-library/creation-library'
import { ImageLibrary } from '@/components/element-library/image-library'
import { VideoLibrary } from '@/components/element-library/video-library'
import { GraphicLibrary } from '@/components/element-library/graphic-library'
import { GifLibrary } from '@/components/element-library/gif-library'
import { StickerLibrary } from '@/components/element-library/sticker-library'
import { AudioLibrary } from '@/components/element-library/audio-library'
import { IconLibrary } from '@/components/element-library/icon-library'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { SearchIcon } from 'lucide-react'
import useDebounce from '@/hooks/use-debounce'
import type { Asset, AssetType } from '@/lib/types'
import { useProjectStore } from '@/hooks/use-project-store'
import { useFlyingAnimation } from '@/hooks/use-flying-animation'
import { AIGeneratorPopup } from '@/components/ai-generator-popup'

export type MediaType = 'image' | 'video'

interface MediaLibraryModalProps {
  onImageSelect?: (imageUrl: string) => void
  onVideoSelect?: (videoUrl: string) => void
  onClose: () => void
  onConfirm?: (mediaUrl: string, mediaType: MediaType) => void
  onCancel?: () => void
  videoMode?: boolean
  // New: show all media types (images + videos) in one unified view
  unifiedMediaMode?: boolean
  initialMediaType?: MediaType
}

export function MediaLibraryModal({
  onImageSelect,
  onVideoSelect,
  onClose,
  onConfirm,
  onCancel,
  videoMode = false,
  unifiedMediaMode = false,
  initialMediaType = 'image',
}: MediaLibraryModalProps) {
  const t = useTranslations('editor')
  const tLibrary = useTranslations('element-library')
  const [activeTab, setActiveTab] = useState('upload')
  // Track the type of media user selected (auto-detected from asset)
  const [selectedMediaType, setSelectedMediaType] = useState<MediaType>(
    videoMode ? 'video' : initialMediaType,
  )

  // Reset current selection when switching tabs
  const handleTabChange = (tab: string) => {
    console.log('Switching to tab:', tab, 'clearing currentSelectedImage')
    setCurrentSelectedImage(null)
    setActiveTab(tab)
  }
  const [dragUrl, setDragUrl] = useState('')
  const [expandedType, setExpandedType] = useState<AssetType | null>(null)
  const [elementSearchQuery, setElementSearchQuery] = useState('')
  const [currentSelectedImage, setCurrentSelectedImage] = useState<
    string | null
  >(null)
  const [showAIGenerator, setShowAIGenerator] = useState(false)

  // Debug logging
  console.log('MediaLibraryModal - currentSelectedImage:', currentSelectedImage)

  const {
    addSelectedAsset,
    selectedAssets,
    removeSelectedAsset,
    setChatBarHighlighting,
  } = useProjectStore()

  const debouncedSearch = useDebounce(elementSearchQuery, 500)
  const { selectedImageUrl, clickPosition, handleSelect, resetAnimation } =
    useFlyingAnimation()

  // Helper to detect if URL is a video based on extension or asset type
  const isVideoUrl = (url: string, assetType?: string): boolean => {
    if (assetType === 'video') return true
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v']
    const lowerUrl = url.toLowerCase()
    return videoExtensions.some((ext) => lowerUrl.includes(ext))
  }

  const handleUrlSubmit = useCallback(
    (url: string) => {
      if (isHttps(url)) {
        console.log('handleUrlSubmit - setting currentSelectedImage to:', url)
        setCurrentSelectedImage(url)

        // Auto-detect media type from URL
        const detectedIsVideo = isVideoUrl(url)
        if (unifiedMediaMode) {
          setSelectedMediaType(detectedIsVideo ? 'video' : 'image')
        }

        if ((detectedIsVideo || videoMode) && onVideoSelect) {
          onVideoSelect(url)
        } else if (onImageSelect) {
          onImageSelect(url)
        }
      }
    },
    [onImageSelect, onVideoSelect, unifiedMediaMode, videoMode],
  )

  const handleAssetSelect = useCallback(
    (e: React.MouseEvent, asset: Asset) => {
      console.log('handleAssetSelect called with asset:', asset)

      // Always set the current selected image for the modal
      setCurrentSelectedImage(asset.link)

      // Auto-detect media type from asset
      const detectedIsVideo = isVideoUrl(asset.link, asset.type)
      if (unifiedMediaMode) {
        setSelectedMediaType(detectedIsVideo ? 'video' : 'image')
      }

      if ((detectedIsVideo || videoMode) && onVideoSelect) {
        onVideoSelect(asset.link)
      } else if (onImageSelect) {
        onImageSelect(asset.link)
      }

      console.log(
        'handleAssetSelect - setting currentSelectedImage to:',
        asset.link,
        'detected type:',
        detectedIsVideo ? 'video' : 'image',
      )
    },
    [onImageSelect, onVideoSelect, unifiedMediaMode, videoMode],
  )

  const handleCancel = useCallback(() => {
    // Call cancel logic and let it handle modal closing
    if (onCancel) {
      onCancel()
    } else {
      onClose()
    }
  }, [onCancel, onClose])

  const handleConfirm = useCallback(() => {
    // Apply the selected image and let onConfirm handle modal closing
    if (onConfirm && currentSelectedImage) {
      onConfirm(currentSelectedImage, selectedMediaType)
    } else {
      // Fallback: just close if no onConfirm
      onClose()
    }
  }, [currentSelectedImage, selectedMediaType, onConfirm, onClose])

  // Asset types for the Library tab
  const getLibraryAssetTypes = (): AssetType[] => {
    if (unifiedMediaMode) {
      // Show all media types - both images and videos
      return ['image', 'video', 'graphic', 'gif', 'sticker', 'icon']
    }
    // Legacy behavior
    return videoMode
      ? ['video']
      : ['image', 'graphic', 'gif', 'sticker', 'icon']
  }
  const libraryAssetTypes = getLibraryAssetTypes()

  const renderAssetSection = (type: AssetType) => {
    const componentMap = {
      image: ImageLibrary,
      video: VideoLibrary,
      graphic: GraphicLibrary,
      gif: GifLibrary,
      sticker: StickerLibrary,
      icon: IconLibrary,
    } as const

    const Component = componentMap[type as keyof typeof componentMap]

    if (!Component) return null

    const isExpanded = expandedType === type

    return (
      <div key={type}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-sm font-medium">
              {tLibrary(`asset_types.${type}`)}
            </h2>
            {(type === 'gif' || type === 'sticker') && (
              <img
                src="https://heyboss.heeyo.ai/klipy-logo.jpg"
                alt="Klipy"
                className="h-4"
              />
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpandedType(isExpanded ? null : type)}
          >
            {isExpanded ? 'Back' : 'See all'}
          </Button>
        </div>
        <div
          className={
            isExpanded
              ? `grid ${type === 'icon' ? 'grid-cols-5' : 'grid-cols-2'} gap-4`
              : 'relative'
          }
        >
          {isExpanded ? (
            <Component
              query={debouncedSearch}
              selectedAssets={selectedAssets}
              onSelect={handleAssetSelect}
              isExpanded
            />
          ) : (
            <ScrollArea className="w-full max-w-full" type="scroll">
              <div className="flex gap-4 pb-4 w-max">
                <Component
                  query={debouncedSearch}
                  selectedAssets={selectedAssets}
                  onSelect={handleAssetSelect}
                />
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
      </div>
    )
  }

  return (
    <DialogContent
      className="max-w-3xl w-[95vw]"
      noCloseButton
      onInteractOutside={handleCancel}
    >
      <DialogTitle className="sr-only">{t('choose_an_image')}</DialogTitle>
      <div className="flex flex-col h-full max-h-[80vh] sm:max-h-[60vh] overflow-scroll">
        {/* Section 1: Header */}
        <div className="h-[60px] border-b border-white/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              {unifiedMediaMode
                ? t('choose_media')
                : videoMode
                  ? 'Choose a video'
                  : t('choose_an_image')}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-white hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Section 2 & 3: Split middle area */}
        <div className="flex flex-col sm:flex-row flex-1 min-h-0 sm:h-[440px]">
          {/* Mobile: Horizontal tabs / Desktop: Vertical sidebar */}
          <div className="sm:w-[180px] border-b sm:border-b-0 sm:border-r border-white/20 sm:p-3 flex-shrink-0">
            <div className="flex sm:flex-col gap-0 sm:gap-3 overflow-x-visible">
              <button
                onClick={() => handleTabChange('upload')}
                className={`flex-shrink-0 sm:w-full justify-start gap-2 sm:gap-3 px-3 py-2 sm:py-3 text-left rounded-md flex items-center text-xs sm:text-sm transition-colors ${
                  activeTab === 'upload'
                    ? 'bg-white/10 text-white'
                    : 'text-white/80 hover:bg-white/5 hover:text-white'
                }`}
              >
                <CloudUpload className="w-4 h-4" />
                <span className="whitespace-nowrap">{t('upload')}</span>
              </button>
              <button
                onClick={() => handleTabChange('library')}
                className={`flex-shrink-0 sm:w-full justify-start gap-2 sm:gap-3 px-3 py-2 sm:py-3 text-left rounded-md flex items-center text-xs sm:text-sm transition-colors ${
                  activeTab === 'library'
                    ? 'bg-white/10 text-white'
                    : 'text-white/80 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Library className="w-4 h-4" />
                <span className="whitespace-nowrap">{t('library')}</span>
              </button>
              {/* Show AI generator for images (available in image mode or unified mode) */}
              {(!videoMode || unifiedMediaMode) && (
                <button
                  onClick={() => handleTabChange('ai-generator')}
                  className={`flex-shrink-0 sm:w-full justify-start gap-2 sm:gap-3 px-3 py-2 sm:py-3 text-left rounded-md flex items-center text-xs sm:text-sm transition-colors ${
                    activeTab === 'ai-generator'
                      ? 'bg-white/10 text-white'
                      : 'text-white/80 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="whitespace-nowrap">{t('ai_generator')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Right: Content of the tab */}
          <div className="flex-1 flex flex-col min-h-0 sm:w-[400px]">
            {activeTab === 'upload' && (
              <div className="h-full flex flex-col">
                <div className="flex-1 min-h-[200px] sm:h-[440px] overflow-hidden p-4 sm:p-5">
                  <div className="h-full">
                    <UploadAssets
                      onSelect={handleAssetSelect}
                      videoMode={unifiedMediaMode ? undefined : videoMode}
                      allowBoth={unifiedMediaMode}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'library' && (
              <div className="h-full flex flex-col">
                <div className="h-[50px] px-4 sm:px-5 py-3 sm:py-5 flex-shrink-0">
                  <div className="relative">
                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 tex-white opacity-80" />
                    <Input
                      type="text"
                      placeholder={t('search')}
                      value={elementSearchQuery}
                      onChange={(e) => setElementSearchQuery(e.target.value)}
                      className="pl-8  bg-white/10 rounded-[12px] text-white placeholder-white/50 h-10 text-sm"
                    />
                  </div>
                </div>
                <div className="flex-1 min-h-[200px] sm:h-[390px] overflow-hidden px-4 sm:px-5 py-3 sm:py-5">
                  <div className="h-full overflow-auto rounded space-y-4 no-scrollbar">
                    {expandedType
                      ? renderAssetSection(expandedType)
                      : libraryAssetTypes.map((type) =>
                          renderAssetSection(type),
                        )}
                  </div>
                </div>
              </div>
            )}

            {/* Show AI generator content for images (available in image mode or unified mode) */}
            {(!videoMode || unifiedMediaMode) &&
              activeTab === 'ai-generator' && (
                <div className="h-full flex flex-col">
                  <div className="h-[50px] px-4 sm:px-5 py-3 sm:py-5 flex-shrink-0">
                    <Button
                      onClick={() => {
                        setShowAIGenerator(true)
                      }}
                      className="w-full h-10 bg-white text-black hover:bg-gray-200 text-sm"
                    >
                      {t('ai_image_generator')}
                    </Button>
                  </div>
                  <div className="flex-1 min-h-[200px] sm:h-[390px] overflow-hidden px-4 sm:px-5 py-3 sm:py-5">
                    <div className="h-full">
                      <h3 className="text-sm font-medium text-white mb-4">
                        {t('my_creations')}
                      </h3>
                      <div className="h-[calc(100%-24px)] overflow-auto rounded">
                        <div
                          onClick={(e) => {
                            // Fallback click handler for CreationLibrary
                            const target = e.target as HTMLElement
                            const img = target.closest('img')
                            if (img && img.src) {
                              console.log(
                                'AI Generator fallback click handler - image:',
                                img.src,
                              )
                              setCurrentSelectedImage(img.src)
                              if (onImageSelect) {
                                onImageSelect(img.src)
                              }
                            }
                          }}
                        >
                          <CreationLibrary
                            query=""
                            selectedAssets={selectedAssets}
                            onSelect={(e, asset) => {
                              console.log(
                                'AI Generator onSelect called with:',
                                e,
                                asset,
                              )
                              console.log(
                                'AI Generator - setting currentSelectedImage to:',
                                asset.link,
                              )
                              setCurrentSelectedImage(asset.link)
                              if (onImageSelect) {
                                onImageSelect(asset.link)
                              }
                            }}
                            isExpanded={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Section 4: Footer */}
        <div className="h-[60px] px-4 border-t border-white/20 flex-shrink-0 flex items-center justify-end">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="bg-transparent border-gray-600 text-white hover:bg-gray-800 px-4 h-8 text-sm"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              className="bg-white text-black hover:bg-gray-200 px-4 h-8 text-sm"
              disabled={!currentSelectedImage}
            >
              {t('confirm')}
            </Button>
          </div>
        </div>
      </div>

      {/* AI Generator Popup */}
      {showAIGenerator && (
        <AIGeneratorPopup
          onImageSelect={(imageUrl: string) => {
            console.log(
              'AI Generator popup - setting currentSelectedImage to:',
              imageUrl,
            )
            setCurrentSelectedImage(imageUrl)
            onImageSelect?.(imageUrl)
            setShowAIGenerator(false)
          }}
          onClose={() => setShowAIGenerator(false)}
        />
      )}
    </DialogContent>
  )
}
